import { createContext, useContext, useEffect, useRef, useState } from 'react';

import Media from '@travel/traveler-core/components/Media';
import { useDeviceTypes, useDialogHandler } from '@travel/traveler-core/hooks';
import { IconText } from '@travel/ui';
import cx from '@travel/ui/utils/classnames';
import { isNotEmptyArray } from '@travel/utils';

import NoImageContent from 'favoriteList/components/CustomMedium/NoImageContent';
import Swiper from '../Swiper';

import styles from './imageGallery.module.scss';

type Props = {
  media: {
    url: string;
    description?: string;
  }[];
  className?: string;
  mediaClassName?: string;
  mediaLimit?: number;
  swiperClassName?: string;
  galleryClassName?: string;
  thumbnailClass?: string;
  mediaTitleClass?: string;
  isTitle?: boolean;
  /** Type of displayed gallery
   * slide: swiped-able with dots
   * scroll: scrollable gallery
   * thumbnail: gallery with thumbnails
   */
  galleryType: 'slide' | 'scroll' | 'thumbnail' | 'thumbnail-slide';
  /** Flag to define whether the video is showed as playable on thumbnail or not */
  isPlayableVideo?: boolean;
  /** Flag to define whether the media is expandable to a gallery dialog */
  isGalleryDisable?: boolean;
  /** Flag to define whether the media is object-fit contain */
  isMediaContain?: boolean;
};
type MediaItemRefProps = HTMLDivElement & {
  transform: number;
};
export const enum FetchPriority {
  HIGH = 'high',
  LOW = 'low',
  AUTO = 'auto',
}
const ImageGallery = (props: Props) => {
  const imageThumbnailSize = { pc: '10vw' };
  const {
    media,
    galleryType,
    className,
    swiperClassName,
    galleryClassName,
    mediaClassName,
    mediaLimit,
    thumbnailClass,
    mediaTitleClass,
    isTitle,
    isPlayableVideo = false,
    isGalleryDisable = false,
    isMediaContain = false,
  } = props;

  const [activeIndex, setActiveIndex] = useState(0);
  const mediaItem = useRef<MediaItemRefProps | null>(null);
  const { onOpen } = useDialogHandler(false);
  const hasMedia = isNotEmptyArray(media);
  const isLazyLoadContext = createContext({
    isDisableLazyLoad: false,
    fetchPriority: FetchPriority.LOW as FetchPriority,
  });
  const { isDisableLazyLoad, fetchPriority } = useContext(isLazyLoadContext);
  const mediaClass = cx(
    styles.media,
    mediaClassName,
    isMediaContain && styles.objectFitContain,
    !isGalleryDisable && hasMedia && styles.isExpandable,
  );
  const { isPC, isSP } = useDeviceTypes();

  useEffect(() => {
    const slider = mediaItem.current;
    let isDown = false;
    let startX: number;
    let scrollLeft: number;
    if (slider) {
      slider.addEventListener('mousedown', (e) => {
        isDown = true;
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      });
      slider.addEventListener('mouseleave', () => {
        isDown = false;
      });
      slider.addEventListener('mouseup', () => {
        isDown = false;
      });
      slider.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX) * 3; //scroll-fast
        slider.scrollLeft = scrollLeft - walk;
      });
    }
    if (mediaItem.current && !mediaItem.current.transform) mediaItem.current.transform = 0;
  }, []);
  if (!hasMedia) {
    return <NoImageContent mediaWrapperClassName={cx(styles.wrapper, className)} />;
  }

  const handleOnClick = () => {
    return isGalleryDisable
      ? undefined
      : (e) => {
          onOpen();
          e.stopPropagation();
        };
  };

  const selectedMedia = media[activeIndex];
  const mediaItems = mediaLimit && mediaLimit > 0 ? media.slice(0, mediaLimit) : media;
  const rootClass = galleryType === 'scroll' ? styles.scrollWrapper : styles.wrapper;
  const galleryMedia = mediaItems.map((media, index) => (
    <Media
      key={index}
      media={media}
      isDisableLazyload={isDisableLazyLoad}
      fetchPriority={fetchPriority}
      testId="imageGallery-mediaItems"
      srcsetStartWidth={135}
      className={mediaClass}
      wrapperClassName={cx(styles.mediaClass, galleryClassName)}
      isPlayableVideo={isPlayableVideo}
      sizes={isSP ? '100vw' : '30vw'}
      onClick={handleOnClick}
      shouldPauseVideo={activeIndex !== index}
    />
  ));
  const transformThumbnail = (from: number, to: number) => {
    if (!mediaItem.current) return;
    // only after 5 slides(index=4), we need to apply transform
    const mediaWidth = mediaItem.current.querySelector('img')?.offsetWidth || 0;
    if (to > 4) {
      if (from < to) {
        setTransformValues(mediaItem.current.transform - mediaWidth);
      } else {
        setTransformValues(mediaItem.current.transform + mediaWidth);
      }
    } else {
      setTransformValues(0);
    }
  };
  const onSwiperChange = (from: number, to: number) => {
    if (from !== to) {
      setActiveIndex(to);
      transformThumbnail(from, to);
    }
  };
  const setTransformValues = (value: number) => {
    if (mediaItem.current) {
      mediaItem.current.style.transform = `translate(${value}px)`;
      mediaItem.current.transform = value;
    }
  };

  return (
    <>
      <div className={cx(rootClass, className)} data-testid="imageGallery-wrapper">
        {galleryType !== 'scroll' ? (
          <>
            <Swiper
              paginationType="arrows"
              className={cx(swiperClassName, styles.swiper)}
              arrowClassName={styles.swiperArrow}
              arrowColor="white"
              arrowSize={24}
              keepArrowsAlwaysVisible={galleryType !== 'thumbnail-slide'}
              onChange={(from, to) => {
                onSwiperChange(from, to);
              }}
              initialSlide={activeIndex}
              isCirclePagination={false}
              shouldUpdateOnceVisible
              shouldRenderAllChild={false}
              maxAdjacentChildren={1}
            >
              {galleryMedia}
            </Swiper>
            {galleryType === 'slide' && mediaItems?.length > 1 && (
              <div className={styles.gradient} />
            )}
            {isTitle && isPC && (
              <p className={mediaTitleClass} data-testid="imageGallery-title">
                {selectedMedia.url}
              </p>
            )}
          </>
        ) : (
          galleryMedia
        )}

        {mediaItems?.length > 1 &&
          (galleryType === 'thumbnail' || galleryType === 'thumbnail-slide') && (
            <div className={styles.thumbnailWrapper}>
              <div
                className={cx(
                  styles.thumbnails,
                  galleryType === 'thumbnail-slide' && styles.thumbnailScrollWrapper,
                )}
                ref={mediaItem}
              >
                {mediaItems.map((media, index) => (
                  <Media
                    key={index}
                    media={media}
                    isDisableLazyload={isDisableLazyLoad}
                    testId="imageGallery-mediaThumbnail"
                    className={cx(
                      thumbnailClass,
                      styles.thumbnail,
                      index !== activeIndex && styles.overlay,
                    )}
                    srcsetStartWidth={100}
                    sizes={imageThumbnailSize.pc}
                    onClick={() => setActiveIndex(index)}
                  />
                ))}
              </div>
            </div>
          )}

        {galleryType !== 'scroll' && mediaItems?.length > 1 && (
          <IconText
            text={`${activeIndex + 1}/${mediaItems.length}`}
            className={cx(styles.iconTextClassName, isGalleryDisable && styles.galleryDisabled)}
            textClassName={styles.iconText}
            data-testid="imageGallery-iconText"
            onClick={handleOnClick}
          />
        )}
      </div>
    </>
  );
};

export default ImageGallery;
